/**
 * canvasEditorStore — ce qui reste d'un store d'écran après #1333.
 *
 * Il portait la **présentation documentaire de l'onglet** : gabarit, méta,
 * version, titre, brouillon actif, statut de sonde. Une seule valeur pour
 * plusieurs documents chauds, donc une valeur recopiée dans le scope au
 * parking, reprojetée à la sélection, et arbitrée à chaque rendu contre
 * l'instantané du résident. Tout cela est supprimé : `CanvasDocumentScope` est
 * l'autorité unique du document de sa conversation, et ses consommateurs
 * l'observent directement (`useCanvasDocumentPresentation`,
 * `lireLeMetaDuScope`).
 *
 * Ce qui subsiste ici n'est plus documentaire :
 *
 * - `holderConversationId` — **projection** de la conversation du résident
 *   sélectionné, écrite par `CanvasResidence` et lue par la scène. Jamais une
 *   clé de routage, de capacité ou d'autorisation. Son remplacement par
 *   l'identité de session appartient à #1336/#1337, pas à cette tranche.
 * - `instance` / `ecrireActeGenere` — sans écrivain ni lecteur de production ;
 *   seules deux sondes de mesure y touchent encore, et elles sont les seules
 *   surfaces de `tests/` à importer ce module :
 *   `tests/ws-mocked/mesure-cout-bascule-de-document.spec.ts` (les deux champs)
 *   et `tests/ws-mocked/mesure-cout-pose-blocs.spec.ts` (`ecrireActeGenere`).
 *   Retrait suivi hors de cette tranche (#1359).
 *
 * ⚠️ **Cet inventaire s'entretient à la main, et c'est un risque connu.** Les
 * sondes `mesure-*` sont exclues du projet `ws-mocked` (elles ne tournent pas
 * dans le gate) et `tests/` n'est type-checké par aucun paquet (#1344) : retirer
 * un champ d'ici ne casse RIEN de visible, la sonde jette silencieusement à la
 * prochaine exécution manuelle. Toute suppression dans ce fichier se vérifie
 * donc par un `grep` explicite dans `tests/`, pas par la CI. C'est exactement ce
 * qui a été manqué une première fois sur #1333 (`setTemplateOverride`).
 */

import { create } from 'zustand';
import type { SuperDocInstance } from '@superdoc/react';
import type { CanvasDraftKind, StructuredBlock } from '@hector/shared-types';
import type { ResultatEcriture } from '../editor/canvasWritePoint';
import type {
  CanvasNamedLetterheadResolution,
  CanvasNamedLetterheadResolver,
} from './canvasDocumentScope';

/** @deprecated Import the session-scoped names from `canvasDocumentScope`. */
export type EnTeteResolu = CanvasNamedLetterheadResolution;
/** @deprecated Import the session-scoped name from `canvasDocumentScope`. */
export type ResolveurDEnTeteNomme = CanvasNamedLetterheadResolver;

export interface CanvasTemplateMeta {
  /**
   * Invariant module (CLAUDE.md 2026-07-13) : `null` pour un canvas GÉNÉRÉ
   * (plan/acte streamé, ou brouillon rouvert sans pièce source) — un id
   * inventé serait pris par l'agent pour un uuid de la table `documents` et
   * rejoué dans `load_document_into_canvas`. Seul un Word importé (pièce
   * réelle) porte un uuid ici. Élargi à `string | null` pour Task 11 : un
   * brouillon rouvert 'generated' hydrate `templateOverride` via
   * `activeDraft.sourceDocumentId ?? null`.
   */
  documentId: string | null;
  documentTitle: string;
  /**
   * Conversation à laquelle appartient le gabarit chargé — **une entrée, pas
   * une source de vérité** (#862). Celui qui charge un document déclare ici
   * d'où il vient ; la garde qui neutralise un gabarit étranger vit dans
   * `canvasHolder.ts` et n'est appliquée qu'à un seul endroit depuis #1333,
   * `canvasDocumentMeta.ts`.
   */
  conversationId?: string | null;
  caseId?: string | null;
  sourceFilePath?: string | null;
  /**
   * Kind du brouillon quand le canvas est amorcé par « Ouvrir un brouillon
   * récent » (2026-07-21) : la copie chargée n'a PAS d'`activeDraft` (réservé à
   * la restauration DANS la conversation propriétaire), donc la nature du
   * document se lit ici pour garder un badge fidèle. Non posé par l'ouverture
   * Word ni par le stream (ceux-ci dérivent la nature autrement).
   */
  kind?: CanvasDraftKind;
}

/**
 * Écrit dans le canvas un acte rendu par un job worker (#938) — les blocs
 * sortis du parseur structurel partagé, poussés par le point d'écriture unique.
 */
export type EcrivainDActeGenere = (blocs: readonly StructuredBlock[]) => Promise<ResultatEcriture>;

interface SuperDocCanvasStore {
  instance: SuperDocInstance | null;
  setInstance: (s: SuperDocInstance | null) => void;
  ecrireActeGenere: EcrivainDActeGenere | null;
  setEcrireActeGenere: (ecrivain: EcrivainDActeGenere | null) => void;
  /**
   * La conversation du résident SÉLECTIONNÉ, pour l'affichage seul.
   *
   * Écrit par `CanvasResidence` à la sélection et remis à `null` par l'éviction
   * de compte. Quitter l'assistant **gèle** cette valeur au lieu de l'effacer :
   * le document continue d'appartenir à sa conversation pendant qu'on regarde
   * ailleurs. `null` = aucun résident sélectionné.
   */
  holderConversationId: string | null;
  setHolderConversationId: (conversationId: string | null) => void;
}

export const useCanvasEditorStore = create<SuperDocCanvasStore>((set) => ({
  instance: null,
  setInstance: (s: SuperDocInstance | null) => set({ instance: s }),
  ecrireActeGenere: null,
  setEcrireActeGenere: (ecrivain) => set({ ecrireActeGenere: ecrivain }),
  holderConversationId: null,
  setHolderConversationId: (conversationId) => set({ holderConversationId: conversationId }),
}));
